/**
 * ViewAsCustomerPalette — global staff "View as customer…" command palette.
 *
 * Sprint 5 of the post-launch overhaul. A keyboard-driven (Cmd/Ctrl+K)
 * modal that fuzzy-searches `customers` by full_name / email /
 * readable_id, lets the staff member pick one, calls
 * `pax-impersonate-issue`, and opens the pax portal in a new tab with
 * the impersonation token attached.
 *
 * Access: gated by the host (`DashboardLayout`) on staff role —
 * admin / super_admin / manager only. The edge function enforces the
 * same on the server.
 *
 * UX choices:
 *   - Opens on Cmd/Ctrl+K from anywhere in the staff app.
 *   - 8 results max, ordered by most-recently-active first.
 *   - Arrow keys to navigate, Enter to issue + open.
 *   - Banner explains the session is read-only + 30-minute TTL.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowRight,
  Eye,
  Loader2,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { usePrivacyMode } from '../../hooks/usePrivacyMode';
import { maskEmailAddress, maskPersonName } from '../../utils/privacyMasking';

interface CustomerHit {
  id: string;
  /** Display name derived from customers.name (or first + last name). */
  full_name: string | null;
  primary_email: string | null;
  readable_id: number | null;
  /** Optional pax_user_id presence flag — surfaced as "claimed" chip so the
   * agent knows if the customer has actually completed pax signup. */
  pax_user_id: string | null;
}

interface RawCustomerRow {
  id: string;
  readable_id: number | null;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  pax_user_id: string | null;
}

function toHit(row: RawCustomerRow): CustomerHit {
  const joined = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  return {
    id: row.id,
    full_name: (row.name && row.name.trim()) || joined || null,
    primary_email: row.email,
    readable_id: row.readable_id,
    pax_user_id: row.pax_user_id,
  };
}

interface Props {
  /** When true, the palette is visible. */
  open: boolean;
  onClose: () => void;
}

const DEBOUNCE_MS = 180;

export function ViewAsCustomerPalette({ open, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [issuing, setIssuing] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const privacy = usePrivacyMode();

  // Reset state when the palette is dismissed.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setActive(0);
      setError(null);
    } else {
      // Focus the input on open. setTimeout dodges the Radix/portal mount race.
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search. Runs the moment the modal opens (with empty query →
  // recent customers).
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        // PT-AUDIT-0029: the real `customers` columns are name / email /
        // readable_id (number). The old query used full_name, primary_email
        // and readable_id_text, which do not exist, so every search failed
        // and the failure was swallowed into "No customers match".
        let q = supabase
          .from('customers')
          .select('id, readable_id, name, first_name, last_name, email, pax_user_id')
          .is('archived_at', null)
          .order('updated_at', { ascending: false })
          .limit(20);

        const raw = query.trim();
        if (raw.length > 0) {
          // Strip characters that would break the PostgREST or() grammar.
          const term = raw.replace(/[,()%*\\]/g, ' ').replace(/\s+/g, ' ').trim();
          if (term.length > 0) {
            const like = `%${term}%`;
            const digits = term.replace(/^#/, '');
            if (/^\d+$/.test(digits)) {
              q = q.or(`readable_id.eq.${Number(digits)},name.ilike.${like}`);
            } else {
              q = q.or(
                `name.ilike.${like},first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like}`,
              );
            }
          }
        }

        const { data, error: dbErr } = await q;
        if (dbErr) {
          setResults([]);
          setError(`Customer search failed: ${dbErr.message}`);
        } else {
          setResults(((data as unknown as RawCustomerRow[]) || []).map(toHit));
        }
        setActive(0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, open]);

  const issueAndOpen = useCallback(async (customer: CustomerHit) => {
    setIssuing(customer.id);
    setError(null);
    try {
      const portalDomain = window.location.hostname.includes('localhost')
        ? 'http://pax.localhost:5173'
        : 'https://pax.propellertravel.com';
      const { data, error: invokeErr } = await supabase.functions.invoke('pax-impersonate-issue', {
        body: { customer_id: customer.id, portal_domain: portalDomain },
      });
      if (invokeErr) throw invokeErr;
      if (!data?.success || !data?.url) throw new Error(data?.error ?? 'Could not issue impersonation token');
      window.open(data.url, '_blank', 'noopener,noreferrer');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open as customer');
    } finally {
      setIssuing(null);
    }
  }, [onClose]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(results.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      void issueAndOpen(results[active]);
    }
  }, [results, active, issueAndOpen, onClose]);

  const subtitle = useMemo(() => (
    query.trim().length === 0 ? 'Recent customers' : `Results for "${query.trim()}"`
  ), [query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50">
          <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center">
            <Eye className="w-4 h-4 text-indigo-700 dark:text-indigo-200" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900 dark:text-white">View as customer</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Read-only, 30-min token, audited
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search by name, email, or customer #…"
            className="w-full pl-10 pr-4 py-3 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none border-b border-slate-100 dark:border-slate-800"
          />
        </div>

        <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-slate-400 font-mono flex items-center justify-between">
          <span>{subtitle}</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {results.length === 0 && !loading ? (
            <div className="px-4 py-8 text-sm text-slate-500 text-center">
              {error
                ? 'The search could not run. See the message below.'
                : 'No customers match. Try a CRM number, email, or part of the name.'}
            </div>
          ) : (
            <ul className="py-1">
              {results.map((c, i) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => void issueAndOpen(c)}
                    disabled={issuing === c.id}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                      i === active
                        ? 'bg-indigo-50 dark:bg-indigo-500/10 text-slate-900 dark:text-slate-100'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {privacy
                          ? maskPersonName(c.full_name || c.primary_email)
                          : c.full_name || c.primary_email || '(unnamed)'}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono truncate">
                        {privacy ? maskEmailAddress(c.primary_email, '—') : c.primary_email || '—'} · #{c.readable_id ?? c.id.slice(0, 8)}
                      </div>
                    </div>
                    {c.pax_user_id ? (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-mono">claimed</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-300 font-mono">not&nbsp;claimed</span>
                    )}
                    {issuing === c.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-500/10 text-xs text-red-700 dark:text-red-300 border-t border-red-200 dark:border-red-500/20">
            {error}
          </div>
        )}

        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wider text-slate-400 font-mono flex items-center justify-between">
          <span>↑↓ navigate · enter open · esc close</span>
          <span>cmd/ctrl + shift + k anywhere</span>
        </div>
      </div>
    </div>
  );
}

/**
 * useViewAsCustomerHotkey — wire Cmd/Ctrl+Shift+K to open the palette
 * globally. Shift is required to avoid clashing with the
 * MasterSearchPalette which already owns plain Cmd/Ctrl+K.
 *
 * Consume as: `const [open, setOpen] = useViewAsCustomerHotkey()`.
 */
export function useViewAsCustomerHotkey(): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return [open, setOpen];
}
